import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from 'react-helmet-async';
import { Container } from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from 'sweetalert2';

import { Url, awsFileUrl } from "../url/url";

const TYPE_OPTIONS = ["Dog", "Cat"];
const SIZE_OPTIONS = ["Small", "Medium", "Large", "Giant"];
const ENERGY_OPTIONS = ["Low Energy", "Medium Energy", "High Energy"];

const AllPetList = () => {
    const navigate = useNavigate();

    const [breeds, setBreeds] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errorMsg, setErrorMsg] = useState("");

    const [typeFilter, setTypeFilter] = useState("");
    const [sizeFilter, setSizeFilter] = useState("");
    const [energyFilter, setEnergyFilter] = useState("");
    const [searchTerm, setSearchTerm] = useState("");

    const [deletingId, setDeletingId] = useState(null);

    const fetchBreeds = useCallback(async () => {
        setLoading(true);
        setErrorMsg("");
        try {
            const params = {};
            if (typeFilter) params.type = typeFilter;
            if (sizeFilter) params.size = sizeFilter;
            if (energyFilter) params.energyLevel = energyFilter;

            const res = await axios.get(`${Url}/api/pet-breed`, { params });
            setBreeds(res.data?.data || []);
        } catch (error) {
            console.error("Error fetching pet breeds:", error);
            setErrorMsg(
                error?.response?.data?.message || "Could not load breeds. Please try again."
            );
        } finally {
            setLoading(false);
        }
    }, [typeFilter, sizeFilter, energyFilter]);

    useEffect(() => {
        fetchBreeds();
    }, [fetchBreeds]);

    const handleDelete = async (id, name) => {
        const result = await Swal.fire({
            title: `Delete "${name}"?`,
            text: "This cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
            cancelButtonText: "Cancel",
            confirmButtonColor: "#dc3545",
        });

        if (!result.isConfirmed) return;

        setDeletingId(id);
        try {
            await axios.delete(`${Url}/api/pet-breed/${id}`);
            setBreeds((prev) => prev.filter((b) => b._id !== id));
            Swal.fire({
                position: "top-end",
                icon: "success",
                title: "Breed deleted",
                showConfirmButton: false,
                timer: 1200,
            });
        } catch (error) {
            console.error("Error deleting breed:", error);
            Swal.fire("Could not delete this breed. Please try again.");
        } finally {
            setDeletingId(null);
        }
    };

    // Resolve a thumbnail URL whether the API already returns a full URL
    // or just the S3 key (matches how products do it elsewhere).
    const resolveImage = (img) => {
        if (!img) return null;
        return img.startsWith("http") ? img : `${awsFileUrl}/${img}`;
    };

    const filteredBreeds = breeds.filter((b) =>
        b.name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

    return (
        <>
            <Helmet>
                <title> Dashboard: Pet Breed Guide </title>
            </Helmet>

            <Container>
                <style>{`
                    .pb-thumb {
                        width: 56px;
                        height: 56px;
                        border-radius: 10px;
                        object-fit: cover;
                        background: #f1f1f4;
                    }
                    .pb-table thead th {
                        background-color: #f3f5fb;
                        font-weight: 600;
                        font-size: 0.85rem;
                        white-space: nowrap;
                    }
                    .pb-table tbody td {
                        vertical-align: middle;
                        font-size: 0.88rem;
                    }
                    .pb-chip {
                        display: inline-block;
                        font-size: 0.72rem;
                        font-weight: 600;
                        padding: 3px 10px;
                        border-radius: 999px;
                    }
                    .pb-chip-type { background: #e8edff; color: #2B3FA0; }
                    .pb-chip-size { background: #fde6f0; color: #e0529c; }
                    .pb-chip-energy { background: #e6f4ea; color: #1e7e34; }
                `}</style>

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-4">
                    <div className="text-primary">
                        <h2 className="text-capitalize mb-0">Pet Breed Guide</h2>
                        <p className="text-secondary mb-0" style={{ fontSize: 14 }}>
                            Informational breed listings — no pricing, lead-gen via enquiries only.
                        </p>
                    </div>
                    <Link to="/dashboard/add-pet" className="btn btn-primary">
                        + Add New Breed
                    </Link>
                </div>

                {/* Filters */}
                <div className="row g-2 mb-3">
                    <div className="col-12 col-md-3">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    <div className="col-6 col-md-3">
                        <select
                            className="form-select"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                        >
                            <option value="">All Types</option>
                            {TYPE_OPTIONS.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-6 col-md-3">
                        <select
                            className="form-select"
                            value={sizeFilter}
                            onChange={(e) => setSizeFilter(e.target.value)}
                        >
                            <option value="">All Sizes</option>
                            {SIZE_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                    <div className="col-6 col-md-3">
                        <select
                            className="form-select"
                            value={energyFilter}
                            onChange={(e) => setEnergyFilter(e.target.value)}
                        >
                            <option value="">All Energy Levels</option>
                            {ENERGY_OPTIONS.map((en) => (
                                <option key={en} value={en}>{en}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {errorMsg && <div className="alert alert-danger">{errorMsg}</div>}

                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                    </div>
                ) : filteredBreeds.length === 0 ? (
                    <div className="text-center text-secondary py-5 bg-light rounded">
                        No breeds found.
                    </div>
                ) : (
                    <div className="bg-white rounded shadow-sm border p-3">
                        <div className="table-responsive">
                            <table className="table pb-table mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Photo</th>
                                        <th>Name</th>
                                        <th>Type</th>
                                        <th>Size</th>
                                        <th>Energy</th>
                                        <th>Height</th>
                                        <th>Weight</th>
                                        <th>Added</th>
                                        <th className="text-end">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredBreeds.map((breed) => (
                                        <tr key={breed._id}>
                                            <td>
                                                {breed.thumbnailImage ? (
                                                    <img
                                                        src={resolveImage(breed.thumbnailImage)}
                                                        alt={breed.altTag || breed.name}
                                                        className="pb-thumb"
                                                    />
                                                ) : (
                                                    <div className="pb-thumb" />
                                                )}
                                            </td>
                                            <td className="fw-semibold">{breed.name}</td>
                                            <td>
                                                <span className="pb-chip pb-chip-type">{breed.type}</span>
                                            </td>
                                            <td>
                                                <span className="pb-chip pb-chip-size">{breed.size}</span>
                                            </td>
                                            <td>
                                                <span className="pb-chip pb-chip-energy">{breed.energyLevel}</span>
                                            </td>
                                            <td className="text-secondary">{breed.height || "—"}</td>
                                            <td className="text-secondary">{breed.weight || "—"}</td>
                                            <td className="text-secondary small">
                                                {breed.createdAt
                                                    ? new Date(breed.createdAt).toLocaleDateString()
                                                    : "—"}
                                            </td>
                                            <td className="text-end">
                                                <div className="d-flex justify-content-end gap-2">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-primary"
                                                        onClick={() => navigate(`/dashboard/edit-pet-breed/${breed._id}`)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-outline-danger"
                                                        disabled={deletingId === breed._id}
                                                        onClick={() => handleDelete(breed._id, breed.name)}
                                                    >
                                                        {deletingId === breed._id ? "Deleting..." : "Delete"}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </Container>
        </>
    )
}

export default AllPetList